import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Typography,
  Radio,
  Tag,
  message,
  Row,
  Col,
  Descriptions,
  Space,
} from "antd";
import { QuestionsType, statusEnum } from "../../Constant/Enums";
import { postAPIExamQueriesUpdateExamQueryStatus } from "../../Generated/orval-apis/exam-queries/exam-queries";
import { ExamQueriesListRes } from "../../Generated/orval-apis/cit.schemas";

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

interface Props {
  open: boolean;
  onClose: () => void;
  setRefresh: () => void;
  record: ExamQueriesListRes | null;
}

const statusOptions = [
  { label: "Pending", value: statusEnum.Pending, disabled: true },
  { label: "Approved", value: statusEnum.Approve },
  { label: "Rejected", value: statusEnum.Reject },
];

const ExamReviewModal: React.FC<Props> = ({
  open,
  onClose,
  setRefresh,
  record,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [form] = Form.useForm();
  const answer = record?.answer;

  const renderAnswerSection = () => {
    switch (answer?.questionsType) {
      case QuestionsType.MCQ:
        return (
          <>
            <Radio.Group value={answer?.studentAnswer} disabled>
              <Space direction="vertical">
                {answer?.options?.map((opt) => (
                  <Radio key={opt.uuId} value={opt.uuId}>
                    {opt.label}{" "}
                    {opt.isCorrect && <Tag color="green">Correct</Tag>}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </>
        );
      case QuestionsType.LongAnswer: // Long Answer
      case QuestionsType.Program: // Program
        return (
          <>
            <Text strong>Student's Answer:</Text>
            {/* <pre style={{ background: "#f5f5f5", padding: 12 }}>
              {answer?.studentAnswer}
            </pre> */}
            <pre
              style={{
                background: "#f5f5f5",
                padding: 12,
                borderRadius: 4,
                overflowX:
                  answer?.questionsType === QuestionsType.Program
                    ? "auto"
                    : "hidden",
                whiteSpace:
                  answer?.questionsType === QuestionsType.Program
                    ? "pre"
                    : "pre-wrap",
              }}
            >
              {answer?.studentAnswer}
            </pre>
          </>
        );
      default:
        return <Text type="secondary">Unsupported question type</Text>;
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        queryId: record?.id,
        status: values.status,
        key: values.key,
        remark: values.remark.trim(),
        mark: values.mark,
      };
      const res = await postAPIExamQueriesUpdateExamQueryStatus(payload);
      if (res.status && res.result) {
        message.success("Status updated successfully");
        setRefresh();
        handleCancel();
      }
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    if (record?.id) {
      form.setFieldsValue({
        remark: record.remark || "",
        status: record.status,
        key: record.answer?.key,
        mark: answer?.obtainedMarks,
      });
    }
  }, [record]);

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      title={null}
      width={700}
      okText="Submit"
      okButtonProps={{
        disabled: record?.status !== statusEnum.Pending,
      }}
      confirmLoading={loading}
      style={{ top: 30 }}
    >
      <div style={{ textAlign: "left", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Re-Evaluation
        </Title>
      </div>
      <Descriptions
        bordered
        size="small"
        column={1}
        style={{ marginBottom: 16 }}
        labelStyle={{ width: "90px" }}
        contentStyle={{ width: "calc(100% - 100px)" }}
      >
        <Descriptions.Item label="Student">
          {record?.student?.name || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Batch">
          {record?.batch?.name || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Subject">
          {record?.subject?.name || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Marks">
          <Tag color="blue">
            {answer?.obtainedMarks} / {answer?.questionMarks}
          </Tag>
        </Descriptions.Item>
      </Descriptions>

      <Title level={5}>
        Q:{answer?.key ? `${answer?.key}  ` : ""} {answer?.questionLabel}
      </Title>

      <div style={{ marginBottom: 20 }}>{renderAnswerSection()}</div>

      <Paragraph>
        <Text strong>Student’s Reason:</Text>
        <Paragraph
          type="secondary"
          style={{ background: "#fafafa", padding: 12, borderRadius: 4 }}
        >
          {record?.reason || <i>No reason provided</i>}
        </Paragraph>
      </Paragraph>

      <Form
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        disabled={record?.status !== statusEnum.Pending}
      >
        <Form.Item
          name="remark"
          label="Teacher Remark"
          rules={[{ required: true, message: "Please enter your remark" }]}
        >
          <TextArea rows={3} placeholder="Enter your response..." />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="mark"
              label="Updated Marks"
              rules={[
                { required: true, message: "Please enter updated marks" },
                {
                  validator: (_, value) => {
                    if (value > (answer?.questionMarks ?? 0)) {
                      return Promise.reject(
                        new Error(
                          `Marks cannot exceed ${answer?.questionMarks}`
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                type="number"
                placeholder={`Enter marks (Max: ${answer?.questionMarks})`}
                min={0}
                max={answer?.questionMarks}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="status"
              label="Update Status"
              rules={[{ required: true, message: "Please select a status" }]}
            >
              <Select placeholder="Select Action" options={statusOptions} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ExamReviewModal;
